/**
 * Unified Document View Page for Consumer Portal
 *
 * Displays a document using shared design-system-ui components:
 * - DocumentRenderer: Renders PDF, images, or unsupported fallback
 * - DocumentAttributesPanel: Right sidebar with file info, AI findings, case links
 * - DocumentViewLayout: Responsive 75/25 split (desktop), stacked (tablet), tab toggle (mobile)
 *
 * Replaces the old AnimatedModal preview in DocumentsPage and the legacy DocumentDetailPage.
 *
 * Issue #1163 - Unified Document View
 */

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import LinkIcon from '@mui/icons-material/Link';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DocumentAttributesPanel,
  DocumentRenderer,
  type DocumentViewCaseLink,
  type DocumentViewFinding,
  DocumentViewLayout,
  type DocumentViewRejection,
  downloadBase64File,
  GlassAlert,
  GlassButton,
  MagicSelect,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageTemplate } from '../../components/layout/PageTemplate';
import {
  useCases,
  useDeleteDocument,
  useDocumentDownload,
  useDocumentFindings,
  useLinkToCase,
  useUploadDocument,
} from '../../hooks';
import type { CaseLinkInfo, ConsumerDocument } from '../../hooks/useDocuments';
import { useConsumerDocument } from '../../lib/trpc/hooks';
import { formatFileSize } from '../../lib/utils/format-number';

const REUPLOADABLE_VERIFICATION_STATUSES: ReadonlySet<string> = new Set(['REJECTED', 'NEEDS_REUPLOAD']);

export default function DocumentViewPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { documentId } = useParams<{ documentId: string }>();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkCaseId, setLinkCaseId] = useState<string>('');
  const [linkSuccess, setLinkSuccess] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch document metadata — try consumer documents first (owned docs),
  // fall back to dataRoom download for linked docs from other sources (Issue #1163 bug fix)
  const { data: rawDocument, isLoading: docLoading, error: docError } = useConsumerDocument(documentId ?? null);

  const doc = rawDocument as ConsumerDocument | undefined;

  // Fetch document content for rendering — use dataRoom.downloadDocument which works for
  // both consumer-owned docs AND data room linked docs (business uploads, entity attachments)
  const { data: downloadData, isLoading: downloadLoading } = useDocumentDownload(doc?.documentId ?? documentId ?? null);

  // Fetch AI findings (confidence scores, extracted fields)
  const { data: findingsData, isLoading: findingsLoading } = useDocumentFindings(doc?.id ?? null);

  // Delete mutation
  const deleteMutation = useDeleteDocument();

  // Link to Case mutation
  const linkToCaseMutation = useLinkToCase();

  // Re-upload mutation
  const uploadMutation = useUploadDocument();

  // Fetch consumer's cases — only when link dialog opens
  const { data: casesResponse, isLoading: casesLoading } = useCases(1, 50, {
    enabled: linkDialogOpen,
  });
  const cases = (casesResponse?.cases ?? []) as Array<{
    id: string;
    title: string;
    caseNumber: string;
    status: string;
  }>;
  const consumerId = casesResponse
    ? ((casesResponse as Record<string, unknown>).consumerId as string | undefined)
    : undefined;

  // Map findings to the DocumentViewFinding shape expected by the panel
  const findings: DocumentViewFinding[] = useMemo(
    () =>
      (findingsData?.findings ?? []).map((f) => ({
        id: f.id,
        title: f.title,
        confidence: f.confidence,
        extractedValue: (f.extractedData?.value as string) ?? undefined,
      })),
    [findingsData],
  );

  // Map case links to the DocumentViewCaseLink shape
  const caseLinks: DocumentViewCaseLink[] = useMemo(
    () =>
      (doc?.caseLinks ?? []).map((link: CaseLinkInfo) => ({
        caseId: link.caseId,
        caseNumber: link.caseNumber,
        verificationStatus: link.verificationStatus,
      })),
    [doc?.caseLinks],
  );

  // Re-upload handler: open file picker, then upload as replacement
  const handleReuploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleReuploadFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !doc) return;

      // Find the rejected attachment ID for replacement
      const rejectedLink = (doc.caseLinks ?? []).find((link: CaseLinkInfo) =>
        REUPLOADABLE_VERIFICATION_STATUSES.has(link.verificationStatus),
      );

      try {
        await uploadMutation.mutateAsync({
          file,
          caseId: rejectedLink?.caseId,
          category: doc.category ?? undefined,
          replacesAttachmentId: rejectedLink?.attachmentId,
        });
        // Navigate back to documents list after successful re-upload
        navigate('/documents');
      } catch (err) {
        setActionError(err instanceof Error ? err.message : t('documents.errors.reuploadFailed'));
      }
      // Reset file input so the same file can be re-selected
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [doc, uploadMutation, navigate, t],
  );

  // Build rejection info from case links with REJECTED or NEEDS_REUPLOAD status
  const rejectionInfo: DocumentViewRejection | undefined = useMemo(() => {
    const rejectedLink = (doc?.caseLinks ?? []).find((link: CaseLinkInfo) =>
      REUPLOADABLE_VERIFICATION_STATUSES.has(link.verificationStatus),
    );
    if (!rejectedLink?.rejectionReason) return undefined;
    return {
      reason: rejectedLink.rejectionReason,
      variant:
        rejectedLink.verificationStatus === 'NEEDS_REUPLOAD' ? ('needs_reupload' as const) : ('rejected' as const),
      onReupload: handleReuploadClick,
    };
  }, [doc?.caseLinks, handleReuploadClick]);

  // Download handler: use shared utility to convert base64 to blob and trigger download
  const handleDownload = useCallback(() => {
    if (!downloadData?.content) return;
    downloadBase64File(downloadData.content, downloadData.mimeType, doc?.name ?? 'document');
  }, [downloadData, doc]);

  // Delete handler: confirm, delete, navigate back
  const handleDelete = useCallback(async () => {
    if (!doc?.id) return;
    try {
      await deleteMutation.mutateAsync({ id: doc.id });
      navigate('/documents');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('documents.errors.deleteFailed'));
    }
  }, [doc?.id, deleteMutation, navigate, t]);

  const handleBack = useCallback(() => {
    // Navigate back to wherever the user came from (loan page, data room, documents list, etc.)
    navigate(-1);
  }, [navigate]);

  // Link to Case handler
  const handleLinkToCase = useCallback(async () => {
    if (!doc?.documentId || !linkCaseId || !consumerId) return;
    try {
      await linkToCaseMutation.mutateAsync({
        documentId: doc.documentId,
        caseId: linkCaseId,
        sourceEntityType: 'CONSUMER',
        sourceEntityId: consumerId,
      });
      setLinkDialogOpen(false);
      setLinkCaseId('');
      setLinkSuccess(true);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('documents.errors.linkFailed'));
    }
  }, [doc?.documentId, linkCaseId, linkToCaseMutation, consumerId, t]);

  // Open link dialog — if single case, link directly
  const handleOpenLinkDialog = useCallback(() => {
    setLinkDialogOpen(true);
    setLinkCaseId('');
  }, []);

  // Auto-dismiss linkSuccess after 4 seconds with proper cleanup
  useEffect(() => {
    if (!linkSuccess) return;
    const timer = setTimeout(() => setLinkSuccess(false), 4000);
    return () => clearTimeout(timer);
  }, [linkSuccess]);

  // Determine loading / error states
  // If consumer doc fetch fails but download data is available (data room linked doc), still render
  const isLoading = docLoading && !downloadData;
  const errorMessage = docError && !downloadData ? docError.message : undefined;

  return (
    <PageTemplate
      variant="detail"
      title={doc?.name ?? downloadData?.fileName ?? t('documents.view.title')}
      onBack={handleBack}
    >
      {/* Hidden file input for re-upload */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={(e) => void handleReuploadFileSelect(e)}
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
      />

      {/* Action error feedback */}
      {actionError && (
        <GlassAlert severity="error" visible={true} onClose={() => setActionError(null)} sx={{ mb: 2 }}>
          {actionError}
        </GlassAlert>
      )}

      {/* Link success feedback */}
      {linkSuccess && (
        <GlassAlert severity="success" visible={true} onClose={() => setLinkSuccess(false)} sx={{ mb: 2 }}>
          {t('documents.view.linkSuccess')}
        </GlassAlert>
      )}

      <Box sx={{ height: 'calc(100vh - 200px)', minHeight: 500 }}>
        <DocumentViewLayout
          loading={isLoading}
          error={errorMessage}
          renderer={
            <DocumentRenderer
              content={downloadData?.content ?? null}
              mimeType={downloadData?.mimeType ?? doc?.mimeType ?? 'application/octet-stream'}
              fileName={doc?.name ?? downloadData?.fileName ?? 'document'}
              loading={downloadLoading}
              onDownload={handleDownload}
            />
          }
          panel={
            doc || downloadData ? (
              <DocumentAttributesPanel
                document={{
                  id: doc?.id ?? documentId ?? '',
                  name: doc?.name ?? downloadData?.fileName ?? 'document',
                  mimeType: doc?.mimeType ?? downloadData?.mimeType ?? 'application/octet-stream',
                  size: doc?.size ?? 0,
                  uploadedAt: doc?.uploadedAt ?? '',
                  documentType: doc?.documentType ?? undefined,
                  category: doc?.category ?? undefined,
                  processingStatus: doc?.processingStatus ?? '',
                }}
                findings={{
                  items: findings,
                  loading: findingsLoading,
                }}
                caseLinks={caseLinks.length > 0 ? { items: caseLinks, mode: 'full' } : undefined}
                rejectionInfo={rejectionInfo}
                formatFileSize={formatFileSize}
                actions={
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <GlassButton
                      variant="gradient"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownload}
                      disabled={!downloadData?.content}
                      fullWidth
                    >
                      {t('documents.view.download')}
                    </GlassButton>
                    <GlassButton variant="outlined" startIcon={<LinkIcon />} onClick={handleOpenLinkDialog} fullWidth>
                      {t('documents.view.linkToCase')}
                    </GlassButton>
                    {rejectionInfo && (
                      <GlassButton
                        variant="outlined"
                        color="warning"
                        startIcon={<UploadIcon />}
                        onClick={handleReuploadClick}
                        disabled={uploadMutation.isPending}
                        fullWidth
                      >
                        {t('documents.view.reupload')}
                      </GlassButton>
                    )}
                    <GlassButton variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack} fullWidth>
                      {t('documents.view.backToDocuments')}
                    </GlassButton>
                    <GlassButton
                      color="error"
                      variant="ghost"
                      startIcon={<DeleteIcon />}
                      onClick={() => setDeleteConfirmOpen(true)}
                      fullWidth
                    >
                      {t('documents.view.delete')}
                    </GlassButton>
                  </Box>
                }
              />
            ) : null
          }
        />
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('dialog.deleteTitle', { entity: t('entities.document') })}</DialogTitle>
        <DialogContent>
          <Typography>{t('dialog.confirmDelete', { entity: t('entities.document') })}</Typography>
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={() => setDeleteConfirmOpen(false)}>{t('common.cancel')}</GlassButton>
          <GlassButton
            color="error"
            variant="gradient"
            onClick={() => void handleDelete()}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? t('common.deleting') : t('common.delete')}
          </GlassButton>
        </DialogActions>
      </Dialog>

      {/* Link to Case Dialog */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('documents.linkToCase.action')}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {t('documents.linkToCase.select')}
            </Typography>
            {casesLoading ? (
              <Typography variant="body2" color="textSecondary">
                {t('common.loading')}
              </Typography>
            ) : cases.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                {t('documents.view.noCases')}
              </Typography>
            ) : (
              <MagicSelect
                fullWidth
                label={t('documents.linkToCase.action')}
                value={linkCaseId}
                onChange={(e) => setLinkCaseId(e.target.value as string)}
                options={cases.map((c) => ({
                  value: c.id,
                  label: t('documents.caseLabel', { caseNumber: c.caseNumber, title: c.title }),
                }))}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <GlassButton onClick={() => setLinkDialogOpen(false)}>{t('common.cancel')}</GlassButton>
          <GlassButton
            variant="gradient"
            onClick={() => void handleLinkToCase()}
            disabled={!linkCaseId || linkToCaseMutation.isPending || cases.length === 0}
            startIcon={<LinkIcon />}
          >
            {linkToCaseMutation.isPending ? t('common.saving') : t('documents.linkToCase.action')}
          </GlassButton>
        </DialogActions>
      </Dialog>
    </PageTemplate>
  );
}
